import { el, append } from "../ui/dom";
import { pageHeader, prose, callout, codeBlock } from "../ui/components";
import type { PageRenderer } from "../ui/router";

export const renderAbout: PageRenderer = (outlet) => {
  append(
    outlet,
    pageHeader({
      eyebrow: "About",
      title: "Real structures, in your browser",
      lede: "Probably is an interactive lab for the probabilistic data structures that quietly power databases, caches, analytics, and monitoring. Every visualization runs on an actual, tested implementation — not a mock-up.",
    }),
  );

  append(
    outlet,
    prose(
      el("h2", {}, "Why this exists"),
      el(
        "p",
        {},
        "Bloom filters have a hundred explainers. But the moment you need to count distinct things, estimate frequencies, delete from a filter, or measure set similarity, the good material thins out to dense papers and scattered blog posts. There was no single place to ",
        el("strong", {}, "play"),
        " with the whole family, ",
        el("strong", {}, "size"),
        " them for a real workload, and ",
        el("strong", {}, "copy correct code"),
        ". That's what this is.",
      ),

      el("h2", {}, "How it works"),
      el(
        "p",
        {},
        "The lab is a static site with zero runtime dependencies. The structures live in ",
        el("code", {}, "src/core"),
        " as small, dependency-free TypeScript classes; the pages you're clicking through instantiate those very classes, feed them data, and read out their internal state to draw the pictures. If a chart shows an estimate converging on the truth, that convergence really happened, live, on your machine.",
      ),
      el(
        "p",
        {},
        "Correctness is pinned down by a test suite that checks each structure against exact ground truth — no false negatives from the filters, cardinality within a few percent for HyperLogLog, the never-undercount guarantee for Count-Min, uniform sampling for the reservoir, and so on. The hash is MurmurHash3, verified against its published test vectors.",
      ),

      el("h2", {}, "Use the core library on its own"),
      el("p", {}, "Everything is importable and framework-agnostic:"),
    ),
  );

  append(
    outlet,
    codeBlock(
      `import { HyperLogLog, BloomFilter, CountMinSketch } from "./core";

const seen = BloomFilter.optimal(1_000_000, 0.01);
seen.add("user@example.com");
seen.has("user@example.com"); // → true (probably)

const distinct = new HyperLogLog({ precision: 14 }); // ~12 KB
for (const ip of stream) distinct.add(ip);
distinct.count(); // ≈ number of unique IPs, ±0.8%

const freq = CountMinSketch.create(0.001, 0.01);
freq.add("/api/login");
freq.estimate("/api/login"); // ≥ true count, overcount ≤ ε·N`,
    ),
  );

  append(
    outlet,
    prose(
      el("h2", {}, "Going deeper"),
      el(
        "ul",
        {},
        el("li", {}, "Broder & Mitzenmacher — “Network Applications of Bloom Filters: A Survey.”"),
        el("li", {}, "Fan, Andersen, Kaminsky & Mitzenmacher — “Cuckoo Filter: Practically Better Than Bloom.”"),
        el("li", {}, "Flajolet, Fusy, Gandouet & Meunier — “HyperLogLog: the analysis of a near-optimal cardinality estimation algorithm.”"),
        el("li", {}, "Cormode & Muthukrishnan — “An Improved Data Stream Summary: The Count-Min Sketch and its Applications.”"),
        el("li", {}, "Metwally, Agrawal & El Abbadi — “Efficient Computation of Frequent and Top-k Elements in Data Streams” (Space-Saving)."),
        el("li", {}, "Masson, Rim & Lee — “DDSketch: A Fast and Fully-Mergeable Quantile Sketch with Relative-Error Guarantees.”"),
        el("li", {}, "Broder — “On the resemblance and containment of documents” (MinHash); Leskovec, Rajaraman & Ullman — “Mining of Massive Datasets,” ch. 3 (LSH)."),
        el("li", {}, "Vitter — “Random Sampling with a Reservoir.”"),
      ),
    ),
  );

  append(
    outlet,
    callout(
      "tip",
      el(
        "div",
        {},
        "Found a bug or want another structure (t-digest, cuckoo hash tables, HLL++ bias correction, top-K)? The whole thing is MIT-licensed and meant to be read and forked.",
      ),
    ),
  );
};
